import { Component } from "@theme/component";

/**
 * A custom element that manages the bundle builder interface.
 * Handles flavor selection and box updates.
 *
 * @extends {Component}
 */
class BundleBuilderComponent extends Component {
  requiredRefs = [];

  #selections = {
    box1: null,
    box2: null,
    box3: null,
    box4: null, // Free Box
  };

  #products = new Map();
  #requiredBoxes = 4; // All 4 boxes (Box 1, 2, 3, Free Box) can be selected

  /**
   * Convert a relative URL to an absolute URL
   * @param {string} url - The URL to convert
   * @returns {string} - The absolute URL
   */
  #toAbsoluteUrl(url) {
    if (!url || typeof url !== "string") return "";

    // Already absolute URL
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Protocol-relative URL (starts with //)
    if (url.startsWith("//")) {
      return window.location.protocol + url;
    }

    // Relative URL - convert to absolute
    if (url.startsWith("/")) {
      return window.location.origin + url;
    }

    // Relative path without leading slash
    return window.location.origin + "/" + url;
  }

  /**
   * Find an image URL by alt text tag
   * Looks for images with alt text containing the specified tag
   * @param {object} product - The product object
   * @param {string} altTag - The alt text tag to search for (e.g., "bundle-selector", "bundle-box")
   * @returns {string} - The image URL, or empty string if not found
   */
  #findImageByAltTag(product, altTag) {
    if (!product || !altTag) return "";

    // Check if product has media array (Shopify product JSON may include this)
    if (product.media && Array.isArray(product.media)) {
      const mediaItem = product.media.find((media) => {
        if (media.media_type !== "image") return false;
        const alt = media.alt || "";
        return alt.toLowerCase().includes(altTag.toLowerCase());
      });

      if (mediaItem) {
        const imageUrl = mediaItem.preview_image?.src || mediaItem.src || mediaItem.url || "";
        return this.#toAbsoluteUrl(imageUrl);
      }
    }

    // Check if images array has objects with alt text
    if (product.images && Array.isArray(product.images)) {
      const imageObj = product.images.find((img) => {
        if (typeof img === "string") return false;
        if (img && typeof img === "object") {
          const alt = img.alt || "";
          return alt.toLowerCase().includes(altTag.toLowerCase());
        }
        return false;
      });

      if (imageObj) {
        const imageUrl = imageObj.src || imageObj.url || "";
        return this.#toAbsoluteUrl(imageUrl);
      }
    }

    return "";
  }

  connectedCallback() {
    super.connectedCallback();

    this.#loadProducts();
    this.#setupEventListeners();

    // Initialize form properties after component is connected
    // Use setTimeout to ensure form is available in the DOM
    setTimeout(() => {
      this.#updateFormProperties();
      // Broadcast initial completion state so the sticky footer (and anyone
      // else listening) starts in sync — with 0 filled boxes the bundle is
      // not yet complete.
      this.#updateAddToCartButton();
    }, 100);
  }

  /**
   * Load product data for the flavor grid
   */
  async #loadProducts() {
    // Use getAttribute for data attributes with numbers, as dataset conversion may not work correctly
    const productIds = [this.getAttribute("data-product-id-1"), this.getAttribute("data-product-id-2"), this.getAttribute("data-product-id-3"), this.getAttribute("data-product-id-4")].filter(Boolean);

    if (productIds.length === 0) {
      console.warn("No product IDs configured for bundle builder");
      return;
    }

    try {
      // Fetch product data from Shopify
      // Shopify product JSON endpoint: /products/{handle}.js
      const productPromises = productIds.map(async (productId) => {
        try {
          const url = `/products/${productId}.js`;

          const response = await fetch(url);
          if (!response.ok) {
            console.error(`Failed to fetch product ${productId}: ${response.status} ${response.statusText}`);
            return null;
          }

          const product = await response.json();

          // Shopify product JSON returns featured_image as a string URL
          // Extract featured_image URL and convert to absolute URL
          let featuredImageUrl = "";

          if (product.featured_image) {
            if (typeof product.featured_image === "string") {
              featuredImageUrl = product.featured_image;
            } else if (typeof product.featured_image === "object" && product.featured_image !== null) {
              // Handle image object
              featuredImageUrl = product.featured_image.src || product.featured_image.url || "";
            }
          }

          // Fallback to first variant's featured_image
          if (!featuredImageUrl && product.variants && product.variants.length > 0) {
            const variantImage = product.variants[0].featured_image;
            if (typeof variantImage === "string") {
              featuredImageUrl = variantImage;
            } else if (variantImage && typeof variantImage === "object") {
              featuredImageUrl = variantImage.src || variantImage.url || "";
            }
          }

          // Fallback to first image in images array
          if (!featuredImageUrl && product.images && product.images.length > 0) {
            const firstImage = product.images[0];
            if (typeof firstImage === "string") {
              featuredImageUrl = firstImage;
            } else if (firstImage && typeof firstImage === "object") {
              featuredImageUrl = firstImage.src || firstImage.url || "";
            }
          }

          // Convert to absolute URL to prevent redirect loops
          product.featured_image = this.#toAbsoluteUrl(featuredImageUrl);

          // Process images array and convert to absolute URLs
          if (product.images && Array.isArray(product.images)) {
            product.images = product.images
              .map((img) => {
                let imgUrl = "";
                if (typeof img === "string") {
                  imgUrl = img;
                } else if (img && typeof img === "object") {
                  imgUrl = img.src || img.url || "";
                }
                return this.#toAbsoluteUrl(imgUrl);
              })
              .filter(Boolean);
          }

          return { id: productId, product };
        } catch (error) {
          console.error(`Error loading product ${productId}:`, error);
          return null;
        }
      });

      const products = await Promise.all(productPromises);
      const loadedProducts = products.filter(Boolean);

      loadedProducts.forEach((item) => {
        if (item && item.product) {
          this.#products.set(item.id, item.product);
        }
      });

      this.#renderFlavorGrid();

      // Pre-populate the bundle with one of each flavor so it starts complete
      // and the Add-to-Cart button is live on first paint. Cold paid traffic
      // can buy in one tap; they can still swap any box before adding.
      this.#prefillDefaultBundle();

      // Initialize form properties after products are loaded
      // Use multiple attempts with delays to ensure everything is ready
      let attempts = 0;
      const maxAttempts = 5;
      const tryUpdateFormProperties = () => {
        attempts++;
        this.#updateFormProperties();
        // Try a few more times to catch any late selections
        if (attempts < maxAttempts) {
          setTimeout(tryUpdateFormProperties, 200);
        }
      };
      setTimeout(tryUpdateFormProperties, 300);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  }

  /**
   * Render the flavor grid with product data
   */
  #renderFlavorGrid() {
    const flavorGrid = this.querySelector('[ref="flavorGrid"]');
    if (!flavorGrid) {
      console.warn("Flavor grid not found");
      return;
    }

    const items = flavorGrid.querySelectorAll('[ref="flavorItem"]');

    items.forEach((item, index) => {
      const productId = item.dataset.productId;

      const product = this.#products.get(productId);

      if (!product) {
        console.warn(`Product ${productId} not found in loaded products`);
        // Keep "Loading..." text if product not loaded
        const name = item.querySelector('[ref="flavorName"]');
        if (name && name.textContent === "Loading...") {
          name.textContent = `Product ${productId} not found`;
        }
        return;
      }

      this.#updateFlavorItem(item, product);
    });
  }

  /**
   * Update a flavor grid item with product data
   */
  #updateFlavorItem(item, product) {
    const image = item.querySelector('[ref="flavorImage"]');
    const name = item.querySelector('[ref="flavorName"]');

    if (image) {
      // Get image URL - use alt text tag "bundle-selector" for product selector icon
      let imageUrl = "";

      // Try to find image by alt text tag "bundle-selector"
      imageUrl = this.#findImageByAltTag(product, "bundle-selector");

      // Fallback to featured_image
      if (!imageUrl && product.featured_image) {
        imageUrl = product.featured_image;
      }
      // Fallback to first image from array
      if (!imageUrl && product.images && product.images.length > 0) {
        const firstImage = product.images[0];
        if (typeof firstImage === "string") {
          imageUrl = firstImage;
        } else if (firstImage && typeof firstImage === "object") {
          imageUrl = firstImage.src || firstImage.url || "";
        }
      }
      // Fallback to variant featured_image
      if (!imageUrl && product.variants && product.variants.length > 0) {
        const variantImage = product.variants[0].featured_image;
        if (typeof variantImage === "string") {
          imageUrl = variantImage;
        } else if (variantImage && typeof variantImage === "object") {
          imageUrl = variantImage.src || variantImage.url || "";
        }
      }

      // Convert to absolute URL to prevent redirect loops
      imageUrl = this.#toAbsoluteUrl(imageUrl);

      if (imageUrl) {
        image.src = imageUrl;
        image.alt = product.title || "";
        image.style.display = "";
      } else {
        console.warn(`No image found for product: ${product.title}`);
        image.src = "";
        image.alt = "";
      }
    }

    if (name) {
      name.textContent = product.title || "Unknown Product";
    }

    // Store product data on the item
    item.dataset.productTitle = product.title || "";
    item.dataset.productImage = product.featured_image || product.images?.[0] || "";
  }

  /**
   * Setup event listeners for flavor selection
   */
  #setupEventListeners() {
    // Listen for flavor item clicks
    this.addEventListener("click", (event) => {
      const flavorItem = event.target.closest('[ref="flavorItem"]');
      if (flavorItem) {
        this.#handleFlavorClick(flavorItem);
        return;
      }

      // Handle remove button clicks
      const removeButton = event.target.closest('[ref="removeButton"]');
      if (removeButton) {
        const boxSelector = removeButton.closest(".bundle-box-selector");
        if (boxSelector) {
          const boxNumber = boxSelector.dataset.boxNumber;
          this.#removeSelection(boxNumber);
        }
      }
    });

    // Set up form submit listener immediately (not in setTimeout)
    // This ensures it's ready before any form submission
    // Use a small delay to ensure form is available in DOM
    setTimeout(() => {
      // The form is a descendant of bundle-builder-component, not an ancestor
      let form = this.querySelector("form");
      if (!form) {
        form = this.closest("form");
      }
      if (!form) {
        form = this.querySelector(".bundle-product-form");
      }

      if (form) {
        // Use capture phase with high priority to run before other handlers
        form.addEventListener(
          "submit",
          () => {
            // Update form properties right before submission to ensure latest selections are included
            this.#updateFormProperties();
          },
          { capture: true }
        );
      } else {
        console.warn("Bundle builder: Form not found for submit listener setup");
      }
    }, 50);
  }

  /**
   * Handle flavor item click
   */
  #handleFlavorClick(flavorItem) {
    const productId = flavorItem.dataset.productId;

    // Get product data from loaded products map, or fallback to dataset
    const product = this.#products.get(productId);
    const productTitle = product?.title || flavorItem.dataset.productTitle || "Unknown Product";

    // Get image URL - use alt text tag "bundle-box" for 'added to bundle' icon
    let productImage = "";

    // Try to find image by alt text tag "bundle-box"
    productImage = this.#findImageByAltTag(product, "bundle-box");

    // Fallback to 1st image (index 0)
    if (!productImage && product?.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (typeof firstImage === "string") {
        productImage = firstImage;
      } else if (firstImage && typeof firstImage === "object") {
        productImage = firstImage.src || firstImage.url || "";
      }
    }
    // Fallback to featured_image
    if (!productImage && product?.featured_image) {
      productImage = product.featured_image;
    }
    // Fallback to variant featured_image
    if (!productImage && product?.variants && product.variants.length > 0) {
      const variantImage = product.variants[0].featured_image;
      if (typeof variantImage === "string") {
        productImage = variantImage;
      } else if (variantImage && typeof variantImage === "object") {
        productImage = variantImage.src || variantImage.url || "";
      }
    }
    // Final fallback to dataset
    if (!productImage) {
      productImage = flavorItem.dataset.productImage || "";
    }

    // Convert to absolute URL to prevent redirect loops
    productImage = this.#toAbsoluteUrl(productImage);

    // Find the first empty box
    let targetBox = this.#findEmptyBox();

    if (!targetBox) {
      // All boxes are filled, don't allow more selections
      return;
    }

    // Extract additional product data for richer selection storage
    const product_id = product?.id || null;
    const variant_id = product?.variants && product.variants.length > 0 ? product.variants[0].id : null;
    const sku = product?.variants && product.variants.length > 0 ? product.variants[0].sku || null : null;

    // Bail before creating a selection if the product failed to load or has no
    // usable variant. A null variant_id box is SILENTLY DROPPED by the backend
    // (the shopper pays the bundle price but receives fewer items), so don't
    // store a selection or update the form for it.
    if (!product || !variant_id) {
      return;
    }

    this.#setSelection(targetBox, {
      productId,
      productTitle,
      productImage,
      product_id,
      variant_id,
      sku,
    });

    // Update visual state
    this.#updateBoxDisplay(targetBox, { productTitle, productImage });
    this.#updateFlavorSelection(flavorItem);
    this.#updateAddToCartButton();
  }

  /**
   * Pre-fill every box with one of each configured flavor (Box 1–3 + Free Box),
   * in product-id order, so the bundle starts complete and immediately
   * purchasable. No-ops if the shopper already started building or if the
   * products haven't loaded yet (a later load attempt will fill them).
   */
  #prefillDefaultBundle() {
    if (this.#getFilledBoxCount() > 0) return;

    const flavorGrid = this.querySelector('[ref="flavorGrid"]');
    if (!flavorGrid) return;
    const items = [...flavorGrid.querySelectorAll('[ref="flavorItem"]')];
    if (items.length === 0) return;

    for (let i = 1; i <= this.#requiredBoxes; i++) {
      const boxKey = `box${i}`;
      if (this.#selections[boxKey]) continue;

      // One of each flavor, in order; cycle if fewer flavors than boxes.
      const item = items[(i - 1) % items.length];
      const productId = item?.dataset.productId;
      const product = productId ? this.#products.get(productId) : null;
      if (!product) continue;

      const productTitle = product.title || item.dataset.productTitle || "Unknown Product";

      // Mirror #handleFlavorClick's image resolution (the "bundle-box" icon).
      let productImage = this.#findImageByAltTag(product, "bundle-box");
      if (!productImage && product.images?.length) {
        const first = product.images[0];
        productImage = typeof first === "string" ? first : first?.src || first?.url || "";
      }
      if (!productImage && product.featured_image) productImage = product.featured_image;
      productImage = this.#toAbsoluteUrl(productImage);

      const product_id = product.id || null;
      const variant_id = product.variants?.length ? product.variants[0].id : null;
      const sku = product.variants?.length ? product.variants[0].sku || null : null;

      this.#setSelection(boxKey, { productId, productTitle, productImage, product_id, variant_id, sku });
      this.#updateBoxDisplay(boxKey, { productTitle, productImage });
    }

    this.#updateAddToCartButton();
    this.#updateFormProperties();
  }

  /**
   * Find the first empty box
   */
  #findEmptyBox() {
    // Check all 4 boxes (Box 1, 2, 3, Free Box)
    for (let i = 1; i <= 4; i++) {
      const boxKey = `box${i}`;
      if (!this.#selections[boxKey]) {
        return boxKey;
      }
    }
    return null;
  }

  /**
   * Set a selection for a box
   */
  #setSelection(boxKey, selection) {
    this.#selections[boxKey] = selection;
    this.#updateFormProperties();
  }

  /**
   * Remove a selection
   */
  #removeSelection(boxNumber) {
    const boxKey = `box${boxNumber}`;
    this.#selections[boxKey] = null;
    this.#updateBoxDisplay(boxKey, null);
    this.#updateAddToCartButton();
    this.#updateFormProperties();
  }

  /**
   * Update box display
   */
  #updateBoxDisplay(boxKey, productData) {
    // Extract box number from boxKey (e.g., "box1" -> "1")
    const boxNumber = boxKey.replace("box", "");
    const boxSelector = this.querySelector(`[data-box-number="${boxNumber}"]`);

    if (!boxSelector) {
      console.warn(`Box selector not found for boxKey: ${boxKey}, boxNumber: ${boxNumber}`);
      return;
    }

    const placeholder = boxSelector.querySelector('[ref="placeholder"]');
    const selected = boxSelector.querySelector('[ref="selected"]');
    const productImage = boxSelector.querySelector('[ref="productImageSrc"]');
    const productName = boxSelector.querySelector('[ref="productName"]');
    const boxElement = boxSelector.querySelector(".bundle-box-selector__box");

    if (productData && productData.productTitle) {
      if (placeholder) placeholder.classList.add("hidden");
      if (selected) selected.classList.remove("hidden");
      if (boxElement) boxElement.classList.add("is-selected");
      if (productImage) {
        productImage.src = productData.productImage || "";
        productImage.alt = productData.productTitle || "";
      }
      if (productName) productName.textContent = productData.productTitle;
    } else {
      if (placeholder) placeholder.classList.remove("hidden");
      if (selected) selected.classList.add("hidden");
      if (boxElement) boxElement.classList.remove("is-selected");
      if (productImage) {
        productImage.src = "";
        productImage.alt = "";
      }
      if (productName) productName.textContent = "";
    }
  }

  /**
   * Update flavor selection visual state
   */
  #updateFlavorSelection(selectedItem) {
    // Remove selected class from all items
    const allItems = this.querySelectorAll('[ref="flavorItem"]');
    allItems.forEach((item) => item.classList.remove("selected"));

    // Add selected class to the clicked item
    selectedItem.classList.add("selected");
  }

  /**
   * Update add to cart button state
   */
  #updateAddToCartButton() {
    const filledBoxes = this.#getFilledBoxCount();
    // Look for button with ref attribute first, then fallback to data attribute
    const button = this.querySelector('[ref="addToCartButton"]') || this.querySelector("[data-bundle-add-to-cart-button]");
    // Find button text within the button element or anywhere in the component
    const buttonText = button?.querySelector(".bundle-builder__button-text") || this.querySelector(".bundle-builder__button-text");

    if (!button) return;

    const remaining = this.#requiredBoxes - filledBoxes;

    if (remaining > 0) {
      button.disabled = true;
      if (buttonText) {
        buttonText.textContent = `Select ${remaining} more ${remaining === 1 ? "box" : "boxes"}`;
      }
    } else {
      button.disabled = false;
      if (buttonText) {
        buttonText.textContent = "Add to Gym Bag";
      }
    }

    // Broadcast completion so observers (e.g. the sticky shopping footer) can
    // gate their own UI on whether the bundle is ready to submit.
    document.dispatchEvent(
      new CustomEvent("bundle:completion-changed", {
        detail: { complete: remaining <= 0, remaining, filled: filledBoxes, required: this.#requiredBoxes },
      })
    );
  }

  /**
   * Get count of filled boxes
   */
  #getFilledBoxCount() {
    let count = 0;
    // Count all 4 boxes (Box 1, 2, 3, Free Box)
    for (let i = 1; i <= 4; i++) {
      if (this.#selections[`box${i}`]) {
        count++;
      }
    }
    return count;
  }

  /**
   * Update form properties for cart submission
   */
  #updateFormProperties() {
    // The form is a descendant of bundle-builder-component, not an ancestor
    // So we need to query within this component, not use closest()
    let form = this.querySelector("form");

    // If not found within component, try closest (in case structure is different)
    if (!form) {
      form = this.closest("form");
    }

    // Last resort: try to find form by class
    if (!form) {
      form = this.querySelector(".bundle-product-form");
    }

    if (!form) {
      console.warn("Bundle builder: Form not found when updating form properties", {
        component: this,
        hasFormDescendant: !!this.querySelector("form"),
        closestForm: !!this.closest("form"),
      });
      return;
    }

    const bundleInput = form.querySelector('[ref="bundleProperty"]');
    const checkoutBox1Input = form.querySelector('[ref="checkoutBox1Property"]');
    const checkoutBox2Input = form.querySelector('[ref="checkoutBox2Property"]');
    const checkoutBox3Input = form.querySelector('[ref="checkoutBox3Property"]');
    const checkoutBox4Input = form.querySelector('[ref="checkoutBox4Property"]');

    // Build structured _bundle JSON
    const bundleSlots = [];
    if (this.#selections.box1) {
      bundleSlots.push({
        slot: "box_1",
        variant_id: this.#selections.box1.variant_id || null,
        product_id: this.#selections.box1.product_id || null,
        product_handle: this.#selections.box1.productId || null,
        sku: this.#selections.box1.sku || null,
        title: this.#selections.box1.productTitle || null,
      });
    }
    if (this.#selections.box2) {
      bundleSlots.push({
        slot: "box_2",
        variant_id: this.#selections.box2.variant_id || null,
        product_id: this.#selections.box2.product_id || null,
        product_handle: this.#selections.box2.productId || null,
        sku: this.#selections.box2.sku || null,
        title: this.#selections.box2.productTitle || null,
      });
    }
    if (this.#selections.box3) {
      bundleSlots.push({
        slot: "box_3",
        variant_id: this.#selections.box3.variant_id || null,
        product_id: this.#selections.box3.product_id || null,
        product_handle: this.#selections.box3.productId || null,
        sku: this.#selections.box3.sku || null,
        title: this.#selections.box3.productTitle || null,
      });
    }
    if (this.#selections.box4) {
      bundleSlots.push({
        slot: "bonus",
        variant_id: this.#selections.box4.variant_id || null,
        product_id: this.#selections.box4.product_id || null,
        product_handle: this.#selections.box4.productId || null,
        sku: this.#selections.box4.sku || null,
        title: this.#selections.box4.productTitle || null,
      });
    }

    const bundleData = {
      schema_version: 1,
      bundle_type: "sponsored_athlete",
      slots: bundleSlots,
    };

    // Set hidden input values
    // Set checkout-visible properties (these will display in checkout)
    if (checkoutBox1Input) checkoutBox1Input.value = this.#selections.box1?.productTitle || "";
    if (checkoutBox2Input) checkoutBox2Input.value = this.#selections.box2?.productTitle || "";
    if (checkoutBox3Input) checkoutBox3Input.value = this.#selections.box3?.productTitle || "";
    if (checkoutBox4Input) checkoutBox4Input.value = this.#selections.box4?.productTitle || "";
    if (bundleInput) bundleInput.value = JSON.stringify(bundleData);
  }

  /**
   * Get current selections (for external use)
   */
  get selections() {
    return { ...this.#selections };
  }

  /**
   * Public method to update form properties (for external use)
   * This allows the subscription form to trigger updates when it initializes
   */
  updateFormProperties() {
    this.#updateFormProperties();
  }
}

if (!customElements.get("bundle-builder-component")) {
  customElements.define("bundle-builder-component", BundleBuilderComponent);
}
